/**
 * Pure logic behind the Test / Run dialog.
 *
 * `TriggerWorkflowDialog` is a large component whose interesting parts are
 * decisions, not markup: what the button says, whether a mock actually replaces
 * a live call, what seeds the test-data editor, what payload a trigger needs,
 * and the order of the submit sequence. All of that lives here so it can be
 * exercised directly, with no rendering and no network.
 *
 * Nothing in this module touches React, timers or fetch. The one function with
 * side effects, `runTriggerFlow`, receives them as injected ports, so it can be
 * driven with plain fakes.
 *
 * Sections:
 *   1. Presentation     — what the dialog shows
 *   2. Workflow state   — mock and connection predicates
 *   3. Test data        — what seeds the editor
 *   4. Run decisions    — payload, gating and retry policy
 *   5. Run flow         — the submit sequence
 */

import { isConnectionReferenceComplete } from '@/services/connectionService';
import type { MockNodeInfo } from '@/types/templateMode';
import {
  buildRequestTriggerSample,
  canProvideTestData,
  computeSchemaHash,
  generateSampleFromSchema,
  getTriggerOutputSchema,
  hasDynamicSchema,
} from '@/services/testDataService';
import { canRunBeInvokedWithPayload, canTriggerRunDraft, type TriggerPayload } from '@/services/monitoringApi';
import type {
  BuildTriggerPayloadInput,
  BuildTriggerPayloadResult,
  ResolveTestDataSeedInput,
  RunTriggerFlowInput,
  RunTriggerFlowPorts,
  RunTriggerFlowResult,
  TestDataSeed,
  TriggerKeyValuePair,
} from './types';


// =============================================================================
// PRESENTATION — what the dialog shows
// =============================================================================

/**
 * The submit button's label, which doubles as the dialog's readiness readout.
 *
 * Order matters and is asserted in tests: `isPreparingRevision` wins over every
 * other state because it is the one condition that must never be clicked
 * through (see `isTestDraftGateBlocked`) — a run started against a revision
 * that is not serving yields a 5xx or a stale run. If a lower-priority status
 * masked it, the button would read as actionable while it is disabled.
 */
export function getSubmitButtonLabel({ isSavingDraft, isPending, isPreparingRevision, isDraft, isMock }: {
  isSavingDraft: boolean;
  isPending: boolean;
  isPreparingRevision: boolean;
  isDraft: boolean;
  isMock: boolean;
}): string {
  if (isPreparingRevision) return 'Preparing workflow...';
  if (isSavingDraft) return 'Saving draft...';
  if (isPending) return 'Running...';
  if (isMock) return 'Test Draft';
  if (isDraft) return 'Test Draft';
  return 'Run';
}

/**
 * Whether the draft-mode test data editor should be shown for a given trigger type.
 * Recurrence triggers take no input — the dialog shows an explanatory note instead
 * and still allows firing the trigger via /runDraft to exercise the rest of the workflow.
 */
export const shouldShowDraftPayloadEditor = (triggerType: string): boolean =>
  triggerType !== 'Recurrence';

/**
 * Human-readable name for a trigger type. Unknown types fall through to the raw
 * value rather than rendering blank, so a trigger the portal has no label for is
 * still identifiable in the dropdown.
 */
export const getTriggerTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    Request: 'HTTP Request',
    Manual: 'Manual',
    Recurrence: 'Recurrence (Schedule)',
    ApiConnection: 'API Connection',
    ApiConnectionWebhook: 'Webhook',
  };
  return labels[type] ?? type;
};


// =============================================================================
// WORKFLOW STATE — mock and connection predicates
// =============================================================================

/**
 * True when the action mock entry would actually replace the live call.
 *
 * An entry without mock data, or explicitly disabled, is a no-op: it is stored
 * so the user's choice survives a reload, but the run still hits the real
 * service, so it must not count as mocked.
 */
export const isActionMockActive = (entry: unknown): boolean => {
  if (!entry || typeof entry !== 'object' || !('actionName' in entry)) return false;
  const info = entry as MockNodeInfo;
  return info.mockOutput !== undefined && info.enabled !== false;
};

/**
 * Whether the workflow has at least one mock entry that will actually change
 * the run.
 *
 * A mock run hits `submitDraftRun` with a `trigger` payload and/or per-action
 * `actionOverrides` (see `triggerMockWorkflowRun`). Either of those alone is
 * enough to differentiate the request from a regular draft run, so the toggle
 * should appear when:
 *   - any action mock is active (has mock data AND `enabled !== false`), or
 *   - a trigger mock entry exists (always sent with a mock run — there is no
 *     per-trigger toggle).
 *
 * Action entries that run live (`enabled === false`, or no mock data) are
 * effectively no-ops and don't count.
 */
export const hasEnabledMocks = (mockedNodes: Record<string, unknown>): boolean =>
  Object.values(mockedNodes).some((m) => {
    if (!m || typeof m !== 'object') return false;
    if ('actionName' in m) return isActionMockActive(m);
    // Trigger payload entry: always active.
    return true;
  });

/**
 * Returns true when the node has a connection reference whose target entry is
 * actually configured. Mere presence of a reference key is not enough — template
 * apply writes placeholder shells (empty `connection.id`, no `parameterValues`,
 * no `authentication`) that we must treat as missing. `isConnectionReferenceComplete`
 * is the same check used across the Connections panel and template flows.
 */
export const hasResolvedConnection = (
  nodeId: string,
  mapping: Record<string, string | null | undefined>,
  references: Record<string, unknown>,
  fallbackRefKey?: string | null,
): boolean => {
  const mappedKey = mapping[nodeId];
  const refKey = (typeof mappedKey === 'string' && mappedKey.length > 0)
    ? mappedKey
    : (typeof fallbackRefKey === 'string' && fallbackRefKey.length > 0 ? fallbackRefKey : undefined);
  if (!refKey) return false;
  const reference = (references as Record<string, Parameters<typeof isConnectionReferenceComplete>[0]>)[refKey];
  return isConnectionReferenceComplete(reference);
};


// =============================================================================
// TEST DATA — what seeds the editor
// =============================================================================


/** Pretty-print persisted JSON, falling back to the raw text if it will not parse. */
const prettyPrintOrRaw = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const asEditorJson = (value: unknown): string => JSON.stringify(value, null, 2);

const blankSeed = (): TestDataSeed => ({ testData: EMPTY_JSON_EDITOR_VALUE, schemaHash: '0' });

/**
 * Resolve what the Test data editor should contain for the selected trigger.
 * Never throws: unparseable stored payloads and missing schemas degrade to the
 * empty placeholder rather than failing the dialog.
 */
export const resolveTestDataSeed = ({
  triggerType,
  mockRunEnabled,
  triggerMock,
  storedPayload,
  isSwaggerLoading,
  schemaSample,
  swaggerPaths,
  swaggerDefinitions,
  operationId,
  requestSchema,
}: ResolveTestDataSeedInput): TestDataSeed => {
  // 1. A trigger mock wins outright while mock-run is on — the run will use
  //    that body, so the editor must show it.
  if (mockRunEnabled && triggerMock?.body !== undefined) {
    return { testData: asEditorJson(triggerMock.body), schemaHash: '0' };
  }

  // 2. Whatever the user last ran with. Their own edit outranks any sample.
  if (storedPayload) {
    return {
      testData: prettyPrintOrRaw(storedPayload.payload),
      schemaHash: storedPayload.schemaHash,
    };
  }

  if (canProvideTestData(triggerType)) {
    // Swagger still in flight — placeholder now, re-resolved when it lands.
    if (isSwaggerLoading) return blankSeed();

    // 3. Curated catalog sample. Left at hash '0': there is no structured
    //    schema behind it, the sample itself is the source of truth.
    if (schemaSample) return { testData: asEditorJson(schemaSample), schemaHash: '0' };

    // 4. Generate from the connector's swagger schema.
    if (swaggerPaths && operationId) {
      // Dynamic schemas resolve at runtime, so nothing can be pre-filled.
      if (hasDynamicSchema(swaggerPaths, operationId, triggerType)) return blankSeed();

      const fields = getTriggerOutputSchema(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- testDataService API uses Record<string, any>
        swaggerPaths as Record<string, any>,
        operationId,
        triggerType,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- testDataService API uses Record<string, any>
        swaggerDefinitions as Record<string, any>,
      );
      if (fields.length === 0) return blankSeed();

      return {
        testData: asEditorJson(generateSampleFromSchema(fields)),
        schemaHash: computeSchemaHash(fields),
      };
    }

    return blankSeed();
  }

  // 5. Non-ApiConnection triggers. A template-provided mock body is used even when
  //    the global mock toggle is off, because it is authored sample data.
  if (triggerMock?.body !== undefined) {
    return { testData: asEditorJson(triggerMock.body), schemaHash: '0' };
  }

  // Request/HTTP triggers: derive a body from the trigger's own request schema
  // so the editor is not empty.
  const { sample, hasContent, hash } = buildRequestTriggerSample(requestSchema);
  return hasContent ? { testData: asEditorJson(sample), schemaHash: hash } : blankSeed();
};


// =============================================================================
// RUN DECISIONS — payload, gating and retry policy
// =============================================================================



/** Message shown when the trigger's type cannot be exercised by a draft run. */
export const INVALID_JSON_MESSAGE = 'Invalid JSON format';

/**
 * What the JSON editors are seeded with when there is nothing to pre-fill.
 * Two blank lines so the caret lands inside the braces.
 */
export const EMPTY_JSON_EDITOR_VALUE = '{\n  \n}';

/**
 * Both JSON editors seed an empty-object placeholder rather than an empty
 * string, so "untouched" has three spellings. Treating all of them as no input
 * is what keeps an untouched editor from sending `{}` as a real body.
 */
export const isBlankJsonInput = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed === '' || trimmed === '{}' || trimmed === EMPTY_JSON_EDITOR_VALUE;
};

/**
 * Why a draft run is refused for this trigger, or null when it is allowed.
 * Published runs are never refused here — the check is draft-only.
 */
export const getDraftRunRejection = (triggerType: string, isDraft: boolean): string | null =>
  isDraft && !canTriggerRunDraft(triggerType)
    ? `Trigger type "${triggerType}" does not support draft run. Publish the workflow and trigger it directly.`
    : null;

/**
 * Build the payload for a run, or report which editor holds invalid JSON.
 *
 * Three shapes come out of this:
 *   - Published Request trigger: full customization (method, headers, queries, body).
 *   - Draft run with test data: that JSON as the body.
 *   - Anything else: no payload at all.
 */
export const buildTriggerPayload = ({
  triggerType,
  triggerKind,
  triggerMethod,
  isDraft,
  headers,
  queries,
  body,
  testData,
}: BuildTriggerPayloadInput): BuildTriggerPayloadResult => {
  const isFullPayload = !isDraft && canRunBeInvokedWithPayload(triggerType, triggerKind);

  if (isFullPayload) {
    const named = <T extends TriggerKeyValuePair>(pairs: readonly T[]) => pairs.filter((p) => p.key.trim());
    const toRecord = (pairs: readonly TriggerKeyValuePair[]): Record<string, string> | undefined =>
      named(pairs).length > 0 ? Object.fromEntries(named(pairs).map((p) => [p.key, p.value])) : undefined;

    let parsedBody: unknown;
    if (!isBlankJsonInput(body)) {
      try {
        parsedBody = JSON.parse(body);
      } catch {
        return { ok: false, field: 'body', message: INVALID_JSON_MESSAGE };
      }
    }

    const payload: TriggerPayload = { method: triggerMethod ?? 'POST' };
    const headersRecord = toRecord(headers);
    const queriesRecord = toRecord(queries);
    if (headersRecord) payload.headers = headersRecord;
    if (queriesRecord) payload.queries = queriesRecord;
    if (parsedBody !== undefined) payload.body = parsedBody;

    return { ok: true, payload, persistTestData: false };
  }

  if (isDraft && !isBlankJsonInput(testData)) {
    try {
      return { ok: true, payload: { body: JSON.parse(testData) }, persistTestData: true };
    } catch {
      return { ok: false, field: 'testData', message: INVALID_JSON_MESSAGE };
    }
  }

  return { ok: true, payload: undefined, persistTestData: false };
};

/**
 * Whether the streaming draft trigger is safe to attempt.
 *
 * Streaming POSTs go straight to the ACA container, so a cold / scaled-to-zero
 * revision would hang or return a CORS-blocked 503. The non-streaming trigger
 * goes through the management API, which handles cold start itself. No revision
 * data at all is treated as ready: the caller reads it from the query cache and
 * a miss must not silently disable streaming forever.
 */
export const isContainerReadyForStreaming = (
  latestRevision: { replicas: { ready: number } } | undefined,
): boolean => !latestRevision || latestRevision.replicas.ready > 0;

/**
 * Attempts allowed against the trigger mutation. A draft gets a second try
 * because the runtime may not have picked up the freshly-saved draft file yet
 * and answers 5xx on the first attempt; a published run is already deployed, so
 * a retry would only duplicate the run.
 */
export const getMaxTriggerAttempts = (isDraft: boolean): number => (isDraft ? 2 : 1);

/** Exponential backoff between draft retries: 500ms, 1s, 2s... */
export const getRetryDelayMs = (attempt: number): number => 500 * Math.pow(2, attempt);


// =============================================================================
// RUN FLOW — the submit sequence, effects injected as ports
// =============================================================================


export const runTriggerFlow = async (
  {
    selectedTrigger,
    workflowName,
    isMockRunActive,
    headers,
    queries,
    body,
    testData,
  }: RunTriggerFlowInput,
  ports: RunTriggerFlowPorts,
): Promise<RunTriggerFlowResult> => {
  const isDraft = !!ports.saveDraft;

  const draftRejection = getDraftRunRejection(selectedTrigger.type, isDraft);
  if (draftRejection) {
    return { kind: 'rejected', message: draftRejection };
  }

  if (ports.saveDraft) {
    ports.setPhase('saving-draft');
    try {
      const saveSuccess = await ports.saveDraft();
      if (!saveSuccess) {
        ports.setPhase('idle');
        return { kind: 'rejected', message: 'Failed to save draft before running' };
      }
      await ports.sleep(500);
    } catch (error) {
      ports.setPhase('idle');
      return {
        kind: 'rejected',
        message: `Failed to save draft: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
    ports.setPhase('idle');
  }

  ports.setPhase('running');

  if (isMockRunActive && ports.mockRun) {
    const result = await ports.mockRun(selectedTrigger.name);
    if (result.success) {
      ports.trackEvent('execution.triggered', { workflowName, mock: 'true' });
      return { kind: 'success', ...(result.runId !== undefined && { runId: result.runId }), notify: false };
    }

    const message = result.error ?? 'Failed to run with mocks';
    ports.trackEvent('execution.trigger_failed', { error: message });
    ports.setPhase('idle');
    return { kind: 'error', message };
  }

  const built = buildTriggerPayload({
    triggerType: selectedTrigger.type,
    triggerKind: selectedTrigger.kind,
    triggerMethod: selectedTrigger.method,
    isDraft,
    headers,
    queries,
    body,
    testData,
  });

  if (!built.ok) {
    ports.setPhase('idle');
    return { kind: 'invalid-payload', field: built.field, message: built.message };
  }

  const payload = built.payload;
  if (built.persistTestData) {
    ports.persistTestData();
  }

  if (isDraft && ports.streamingTrigger && isContainerReadyForStreaming(ports.readLatestRevision())) {
    const result = await ports.streamingTrigger(selectedTrigger.name, payload?.body);
    if (result.success) {
      ports.trackEvent('execution.triggered', { workflowName, streaming: 'true' });
      return { kind: 'success', ...(result.runId !== undefined && { runId: result.runId }), notify: false };
    }

    if (!result.useFallback) {
      // The backend may have created a run; falling through could duplicate it.
      ports.setPhase('idle');
      return { kind: 'error', message: result.error };
    }
  }

  const maxAttempts = getMaxTriggerAttempts(isDraft);
  let lastError: string | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = await ports.submitTrigger({
      triggerName: selectedTrigger.name,
      ...(payload && { payload }),
      isDraftMode: isDraft,
    });

    if (result.success) {
      ports.trackEvent('execution.triggered', { workflowName, isDraftMode: String(isDraft) });
      return { kind: 'success', ...(result.runId !== undefined && { runId: result.runId }), notify: true };
    }

    lastError = result.error ?? 'Failed to trigger workflow';

    if (isDraft && attempt < maxAttempts - 1) {
      await ports.sleep(getRetryDelayMs(attempt));
    }
  }

  const message = lastError ?? 'Failed to trigger workflow';
  ports.trackEvent('execution.trigger_failed', { error: message });
  ports.setPhase('idle');
  return { kind: 'error', message };
};
